import type { GraphData, Edge } from '../model/types.ts'
import { NodeType, EdgeType } from '../model/types.ts'

const SCALE = 1 / 50 // Convert world units to ~cm

/** Convert a phase {n, d} to LaTeX notation. */
function phaseToLatex(phase: { n: number; d: number }): string {
  if (phase.n === 0) return ''
  if (phase.d === 1) {
    if (phase.n === 1) return '$\\pi$'
    return `$${phase.n}\\pi$`
  }
  if (phase.n === 1) return `$\\frac{\\pi}{${phase.d}}$`
  return `$\\frac{${phase.n}\\pi}{${phase.d}}$`
}

/**
 * Export a ZX graph as TikZ LaTeX code.
 * Self-contained — only requires \usepackage{tikz}.
 */
export function exportTikZ(graph: GraphData): string {
  // Assign sequential labels
  const idToLabel = new Map<string, string>()
  let idx = 0
  for (const id of graph.nodes.keys()) {
    idToLabel.set(id, `v${idx++}`)
  }

  const lines: string[] = []
  lines.push('% Generated by ZX Sketch')
  lines.push('% Requires: \\usepackage{tikz}')
  lines.push('%')
  lines.push('% Tip: For publication-quality output, consider using')
  lines.push('% \\usepackage{zx-calculus} and its native node styles.')
  lines.push('')
  lines.push('\\begin{tikzpicture}[')
  lines.push('  zxZ/.style={circle, draw=green!60!black, fill=green!30!white, minimum size=7mm, inner sep=0pt, font=\\scriptsize},')
  lines.push('  zxX/.style={circle, draw=red!60!black, fill=red!30!white, minimum size=7mm, inner sep=0pt, font=\\scriptsize},')
  lines.push('  zxN/.style={circle, draw=black, fill=black, minimum size=2mm, inner sep=0pt},')
  lines.push('  zxH/.style={rectangle, draw=yellow!60!black, fill=yellow!50!white, minimum size=3mm, inner sep=0pt},')
  lines.push(']')

  // Nodes
  for (const [id, node] of graph.nodes) {
    const label = idToLabel.get(id)!
    const x = (node.x * SCALE).toFixed(2)
    const y = (-node.y * SCALE).toFixed(2) // Flip Y for LaTeX

    let style: string
    let content: string
    switch (node.type) {
      case NodeType.Z:
        style = 'zxZ'
        content = phaseToLatex(node.phase)
        break
      case NodeType.X:
        style = 'zxX'
        content = phaseToLatex(node.phase)
        break
      case NodeType.Boundary:
      default:
        style = 'zxN'
        content = ''
        break
    }
    lines.push(`  \\node[${style}] (${label}) at (${x}, ${y}) {${content}};`)
  }

  if (graph.edges.size > 0) {
    lines.push('')
  }

  // Group edges by endpoint pair for multi-edge handling
  const edgeGroups = new Map<string, { edges: Edge[]; sourceLabel: string; targetLabel: string }>()
  for (const edge of graph.edges.values()) {
    const sLabel = idToLabel.get(edge.source)!
    const tLabel = idToLabel.get(edge.target)!
    const isSelfLoop = edge.source === edge.target
    const key = isSelfLoop
      ? `self:${sLabel}`
      : (sLabel < tLabel ? `${sLabel}:${tLabel}` : `${tLabel}:${sLabel}`)

    if (!edgeGroups.has(key)) {
      edgeGroups.set(key, { edges: [], sourceLabel: sLabel, targetLabel: tLabel })
    }
    edgeGroups.get(key)!.edges.push(edge)
  }

  // Edges
  for (const [, group] of edgeGroups) {
    const { edges, sourceLabel, targetLabel } = group
    const isSelfLoop = sourceLabel === targetLabel
    const count = edges.length

    for (let i = 0; i < count; i++) {
      const edge = edges[i]
      const hadamard = edge.type === EdgeType.Hadamard ? ' node[zxH, midway] {}' : ''

      if (isSelfLoop) {
        const directions = ['above', 'below', 'left', 'right']
        const dir = directions[i % directions.length]
        lines.push(`  \\draw (${sourceLabel}) to[loop ${dir}]${hadamard} (${sourceLabel});`)
      } else if (count === 1) {
        lines.push(`  \\draw (${sourceLabel}) to${hadamard} (${targetLabel});`)
      } else {
        // Multiple edges: spread with bend
        const bendAngle = (i - (count - 1) / 2) * 25
        if (Math.abs(bendAngle) < 1) {
          lines.push(`  \\draw (${sourceLabel}) to${hadamard} (${targetLabel});`)
        } else {
          const bendDir = bendAngle > 0 ? 'left' : 'right'
          lines.push(`  \\draw (${sourceLabel}) to[bend ${bendDir}=${Math.abs(bendAngle).toFixed(0)}]${hadamard} (${targetLabel});`)
        }
      }
    }
  }

  lines.push('\\end{tikzpicture}')
  lines.push('')
  return lines.join('\n')
}
